import type { ApiAttributionContext } from "@bridge-applications/pb-types";

type Properties = Record<string, string | number | null>;

export function attributionEventProperties(
    context: ApiAttributionContext | null,
): Properties {
    const result: Properties = {
        attribution_status: context?.status ?? "pending",
        attribution_model_version: context?.modelVersion ?? null,
        app_session_id: context?.appSessionId ?? null,
        acq_status: null,
        acq_touch_id: null,
        acq_at: null,
        entry_touch_id: null,
        entry_at: null,
        entry_user_type: null,
        entry_observed_via: null,
        entry_resolution_method: null,
        entry_resolution_status: null,
    };
    const resolved = context?.status === "resolved" ? context : null;
    if (resolved) {
        const { acquisition, entry } = resolved;
        Object.assign(result, {
            acq_status: acquisition.status,
            acq_touch_id: acquisition.touchId,
            acq_at: acquisition.at,
            entry_touch_id: entry.touchId,
            entry_at: entry.at,
            entry_user_type: entry.userType,
            entry_observed_via: entry.observedVia,
            entry_resolution_method: entry.resolutionMethod,
            entry_resolution_status: entry.resolutionStatus,
        });
    }
    for (const [prefix, source] of [
        ["acq", resolved?.acquisition.source],
        ["entry", resolved?.entry.source],
    ] as const) {
        Object.assign(result, {
            [`${prefix}_link_id`]: source?.linkId ?? null,
            [`${prefix}_source`]: source?.source ?? null,
            [`${prefix}_medium`]: source?.medium ?? null,
            [`${prefix}_publisher`]: source?.publisher ?? null,
            [`${prefix}_placement`]: source?.placement ?? null,
            [`${prefix}_campaign_key`]: source?.campaignKey ?? null,
            [`${prefix}_creative_key`]: source?.creativeKey ?? null,
            [`${prefix}_provider`]: source?.provider ?? null,
            [`${prefix}_ad_id`]: source?.externalAdId ?? null,
        });
    }
    return result;
}

/** Read-only SDK projection of authSlice, never persisted or used for app decisions. */
export function createAttributionAnalyticsProjection() {
    let owner: string | null = null;
    let properties = attributionEventProperties(null);
    return {
        update(userId: string | null, context: ApiAttributionContext | null) {
            owner = userId;
            properties = attributionEventProperties(userId ? context : null);
        },
        clear() {
            owner = null;
            properties = attributionEventProperties(null);
        },
        properties(userId: string | null | undefined): Properties {
            return {
                ...(owner && userId === owner
                    ? properties
                    : attributionEventProperties(null)),
            };
        },
    };
}
export const attributionAnalyticsProjection =
    createAttributionAnalyticsProjection();
